
/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import styles from './styles';
import { getConfig } from '../../actions/utils';

// Single source of truth for the built-in FAQ entries. Every entry carries a
// stable `id` so it can be hidden (`faq.hide`) or retitled/relinked
// (`faq.overrides`) per client from Edit Settings; entirely new entries are
// added per client via `faq.custom`. The Edit Settings dashboard derives its
// FAQ controls from this list so the two can never drift.
export const FAQ_LINKS = [
    { id: 'createaccount', title: 'Creating an account', link: 'https://docs.google.com/document/d/10xHTlR28I6bLUVyWX1KZyE6C2eM8BEw-/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'placingorder', title: 'Placing an order', link: 'https://docs.google.com/document/d/1pT-PZb9X5Mot1O4LHFG0nDF-J2OddJlD/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'managingorders', title: 'Managing Orders', link: 'https://docs.google.com/document/d/1OIFzCaOm10yytF8eLsu7lNP796bySZpK/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'shippinglabels', title: 'Shipping Labels', link: 'https://docs.google.com/document/d/1b5eyNo5LowmD8Gf4KmQtQwgcXXyiBkc4/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'addingusers', title: 'Adding Users', link: 'https://docs.google.com/document/d/1hG2mrkRuyeImmqIASU4CH32vE-mtu6Q1/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'checkout', title: 'Check Out Feature', link: 'https://docs.google.com/document/d/1AXKVMGGJG4PcGwsQb4PnA12HWYubfDUJ/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },

    { id: 'chromeext', title: 'How to Download the GPS Chrome Extension', link: 'https://docs.google.com/document/d/1eEx87XbB_m2zbF42Nv_nw3QmTZE_qvHZ/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'manifestcreate', title: 'How to use the Manifest Creation Tool', link: 'https://docs.google.com/document/d/13uzbFkN2eFg_1cQ_6vSbgc1pn_HZCsYN/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'finishedmanifest', title: 'How to Upload a Finished Manifest into GPS One', link: 'https://docs.google.com/document/d/1zw8-I8BMTAaxndbJb7T-lCkKrO4o10wq/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'manifestdownload', title: 'How to use the Manifest Download Feature', link: 'https://docs.google.com/document/d/1wZuTEvn9pAJUA8-xt-QG-BFSvxWukZSr/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'reconciliation', title: 'Reconciliation', link: 'https://docs.google.com/document/d/16i8pIjPfWWHhcfTDKvd0F_0c-OwulIHQ/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'shippingdashboard', title: 'Shipping Dashboard', link: 'https://docs.google.com/document/d/1gPWWN5xdz_YFCGUhPgurzTHNyNaNFr5w/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    { id: 'requiredpayments', title: 'Required Payments', link: 'https://docs.google.com/document/d/1Wu5y08Z2sxY1kuoXXFx0ZjTo2CCINLQT/edit?usp=sharing&ouid=111840744315223401259&rtpof=true&sd=true' },
    // { title: 'How to delete your account', link: 'https://docs.google.com/document/d/1F2jx2pABV8Uky33xWrK0sd-aCCN9HFme/edit?usp=sharing&ouid=103737434464892542058&rtpof=true&sd=true' },
    // { title: 'How to Download and Upload a Manifest to an Order', link: 'https://docs.google.com/document/d/16-0AxFcnY8MVPUxqDLWTC8lhHZMEYXNb/edit?usp=sharing&ouid=109176380852507117745&rtpof=true&sd=true' },
];

// Only http(s) links are ever rendered into an href — a misconfigured setting
// must not be able to turn an FAQ link into a javascript: URL.
export const isFaqLink = (v) => typeof v === 'string' && /^https?:\/\//i.test(v);

// The FAQ entries a client actually sees: built-ins with per-client overrides
// applied and hidden entries removed, followed by the client's custom entries.
// Exported so the settings screen (and tests) can resolve the same list the
// page renders without going through window.APPCONFIG.
export const resolveFaqLinks = ({ hide, overrides, custom } = {}) => {
  const hidden = Array.isArray(hide) ? hide : [];
  const over = overrides && typeof overrides === 'object' && !Array.isArray(overrides) ? overrides : {};
  const extra = Array.isArray(custom) ? custom : [];

  const links = FAQ_LINKS
    .filter(f => !hidden.includes(f.id))
    .map(f => {
      const o = over[f.id] || {};
      return {
        key: f.id,
        id: f.id,
        title: (typeof o.title === 'string' && o.title.trim()) ? o.title : f.title,
        link: isFaqLink(o.link) ? o.link : f.link,
      };
    });

  extra.forEach((c, i) => {
    if (!c || typeof c !== 'object') return;
    const title = typeof c.title === 'string' ? c.title.trim() : '';
    // Incomplete rows are simply not rendered — the operator may still be
    // typing one out when another admin loads the page.
    if (!title || !isFaqLink(c.link)) return;
    links.push({ key: `custom-${i}`, custom: true, title, link: c.link });
  });

  return links;
};

export const FAQ = props => {
  const links = resolveFaqLinks({
    hide: getConfig('faq.hide', []),
    overrides: getConfig('faq.overrides', {}),
    custom: getConfig('faq.custom', []),
  });

  return (
    <div css={css(styles.container)}>
      {links.map(l => {
        return (
          <div key={l.key} className='linkContainer'>
            <div className='title'>{l.title}</div>
            <a className='link' href={l.link} target="_blank" rel="noopener noreferrer">{'Click Here to Learn More'}</a>
          </div>
        )
      })}
    </div>
  )
}
